/**
 * Browser-only assistant TTS: best available local voice + light text cleanup +
 * short phrase queue (no network, no AI tokens).
 */

const DEFAULT_LANG = "en-IN";

function preprocessTextForBrowserTTS(raw) {
  let s = String(raw || "");
  s = s.replace(/\r\n/g, "\n");
  s = s.replace(/•\s*/g, "");
  s = s.replace(/[\u201c\u201d\u2018\u2019"]/g, "");
  s = s.replace(/₹\s*/g, " rupees ");
  s = s.replace(/\s*—\s*/g, ", ");
  s = s.replace(/\n{3,}/g, "\n\n");
  return s.trim();
}

function splitIntoSpeakableChunks(text) {
  const t = String(text || "").trim();
  if (!t) return [];

  const lines = t
    .split(/\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length > 1) {
    return lines.flatMap((line) => splitLongLine(line, 260));
  }
  return splitLongLine(t.replace(/\s+/g, " ").trim(), 260);
}

function splitLongLine(line, maxLen) {
  if (line.length <= maxLen) return [line];
  const out = [];
  let rest = line.trim();
  while (rest.length > maxLen) {
    const win = rest.slice(0, maxLen);
    let breakPos = -1;
    const dot = win.lastIndexOf(". ");
    if (dot >= maxLen * 0.35) breakPos = dot + 2;
    if (breakPos < 0) {
      const semi = win.lastIndexOf("; ");
      if (semi >= maxLen * 0.35) breakPos = semi + 2;
    }
    if (breakPos < 0) {
      const com = win.lastIndexOf(", ");
      if (com >= maxLen * 0.45) breakPos = com + 2;
    }
    if (breakPos < 0) {
      const sp = win.lastIndexOf(" ");
      if (sp > maxLen * 0.55) breakPos = sp + 1;
    }
    if (breakPos < 0) breakPos = maxLen;
    const chunk = rest.slice(0, breakPos).trim();
    if (chunk) out.push(chunk);
    rest = rest.slice(breakPos).trim();
  }
  if (rest) out.push(rest);
  return out;
}

function scoreVoiceForAssistant(v, preferredLang) {
  const name = (v.name || "").toLowerCase();
  const lang = (v.lang || "").toLowerCase().replace("_", "-");
  const pref = (preferredLang || DEFAULT_LANG).toLowerCase();

  let s = 0;
  if (!lang.startsWith("en")) return -1e6;

  if (lang === pref || lang.startsWith(`${pref}-`)) s += 55;
  else if (lang.startsWith("en-in")) s += 50;
  else if (lang.startsWith("en-gb")) s += 22;
  else if (lang.startsWith("en-au")) s += 18;
  else if (lang.startsWith("en-us")) s += 12;
  else s += 8;

  if (/premium|enhanced|personal|natural|neural/i.test(name)) s += 35;
  if (/google|apple|microsoft/i.test(name)) s += 6;
  if (
    /samantha|karen|moira|tessa|veena|rishi|fiona|serena|daniel|thomas|arthur|martha|aaron|sangeeta|lekha/i.test(
      name
    )
  )
    s += 28;
  if (/india|indian|bengali|tamil|hindi/i.test(name)) s += 14;

  if (/compact|low\s*quality|basic/i.test(name)) s -= 25;
  return s;
}

function pickBestLocalVoice(preferredLang) {
  try {
    const voices = window.speechSynthesis.getVoices();
    if (!voices || !voices.length) return null;
    let best = null;
    let bestScore = -1e9;
    for (const v of voices) {
      const sc = scoreVoiceForAssistant(v, preferredLang);
      if (sc > bestScore) {
        bestScore = sc;
        best = v;
      }
    }
    return best;
  } catch {
    return null;
  }
}

/**
 * @param {string} text
 * @param {{
 *   lang?: string,
 *   rate?: number,
 *   pitch?: number,
 *   pauseBetweenMs?: number,
 *   flushDelayMs?: number,
 *   onBeforeSpeak?: () => void,
 *   onEnd?: () => void,
 *   onError?: () => void,
 * }} [opts]
 */
export function speakBrowserAssistantReply(text, opts = {}) {
  if (typeof window === "undefined" || !window?.speechSynthesis || !text) {
    opts.onEnd?.();
    return;
  }

  const lang = opts.lang || DEFAULT_LANG;
  const rate = typeof opts.rate === "number" ? opts.rate : 0.92;
  const pitch = typeof opts.pitch === "number" ? opts.pitch : 1;
  const pauseBetweenMs =
    typeof opts.pauseBetweenMs === "number" ? opts.pauseBetweenMs : 85;
  const flushDelayMs =
    typeof opts.flushDelayMs === "number" ? opts.flushDelayMs : 0;

  const parts = splitIntoSpeakableChunks(preprocessTextForBrowserTTS(text));
  if (!parts.length) {
    opts.onEnd?.();
    return;
  }

  const run = () => {
    try {
      window.speechSynthesis.cancel();
      const voice = pickBestLocalVoice(lang);
      let index = 0;

      const speakNext = () => {
        if (index >= parts.length) {
          opts.onEnd?.();
          return;
        }
        const u = new SpeechSynthesisUtterance(parts[index]);
        if (voice) u.voice = voice;
        u.lang = voice?.lang || lang;
        u.rate = rate;
        u.pitch = pitch;
        u.onerror = () => {
          opts.onError?.();
        };
        u.onend = () => {
          index += 1;
          if (index >= parts.length) {
            opts.onEnd?.();
            return;
          }
          window.setTimeout(speakNext, pauseBetweenMs);
        };
        window.speechSynthesis.speak(u);
      };

      opts.onBeforeSpeak?.();
      if (window.speechSynthesis.paused) window.speechSynthesis.resume();
      speakNext();
    } catch {
      opts.onError?.();
      opts.onEnd?.();
    }
  };

  const voices = window.speechSynthesis.getVoices();
  if (!voices || voices.length === 0) {
    const scheduleRun = () => {
      window.speechSynthesis.removeEventListener("voiceschanged", scheduleRun);
      window.clearTimeout(fallbackTimer);
      window.setTimeout(run, flushDelayMs);
    };
    const fallbackTimer = window.setTimeout(scheduleRun, 520);
    window.speechSynthesis.addEventListener("voiceschanged", scheduleRun);
    return;
  }

  window.setTimeout(run, flushDelayMs);
}

export function cancelBrowserSpeech() {
  if (typeof window !== "undefined" && window.speechSynthesis) {
    window.speechSynthesis.cancel();
  }
}
